import type { EventBusSubscribeHandler } from '@/services/EventService/types/EventBusSubscribeHandler';
import { EventPayload } from '@/services/EventService/types/EventPayload';
import { EventV2 } from '@/services/EventService/abstract/EventV2';

class EventBusObserver {
  private readonly observers: Record<string, EventBusSubscribeHandler<any>[]> = {};

  public subscribe<T extends new (...args: any[]) => EventV2<any>>(
    eventClass: T,
    handler: (payload: EventPayload<T>) => void,
  ): void {
    const eventName = eventClass.name;
    if (!eventName) return this.handleNotEventClassError('subscribe', eventClass);
    if (!handler) return;

    if (!this.observers[eventName]) {
      this.observers[eventName] = [];
    }

    this.observers[eventName].push(handler);
  }

  public unsubscribe<T extends new (...args: any[]) => EventV2<any>>(
    eventClass: T,
    handler: (payload: EventPayload<T>) => void,
  ) {
    const eventName = eventClass.name;
    if (!eventName) return this.handleNotEventClassError('unsubscribe', eventClass);

    if (this.observers[eventName]) {
      this.observers[eventName] = this.observers[eventName].filter((subscriber) => subscriber !== handler);
    }
  }

  public dispatch<DispatchedEvent extends EventV2<any>>(event: DispatchedEvent) {
    const eventName = event.constructor.name;
    if (!eventName) return this.handleNotEventInstanceError('dispatch', event);

    const subscribers = this.observers[eventName];
    if (subscribers?.length) {
      const { data } = event.get();
      subscribers.forEach((handler) => handler(data));
    }
  }

  private handleNotEventClassError(method: string, eventClass: unknown) {
    if (process.env.VITE_EDS_DEBUG) {
      console.warn(`EventService: ${method} received invalid event class.`, { eventClass });
    }
  }

  private handleNotEventInstanceError(method: string, event: unknown) {
    if (process.env.VITE_EDS_DEBUG) {
      console.warn(`EventService: ${method} received invalid event instance.`, { event });
    }
  }
}

const EventService = new EventBusObserver();
export default EventService;
